import React, { useEffect } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Grid, MenuItem } from '@material-ui/core';
import { FormSelect, FormTextField, useDebouncedEffect } from '@lib/common';
import { RoomType } from '@lib/state';
import { FilterSection } from 'app/shared';
import { BulkChangeBillingRunStatus, GetBulkChangeBillingRunsRequest } from 'app/state';

interface Props {
  filter: GetBulkChangeBillingRunsRequest;
  roomTypes: RoomType[];
  onFilterChange: (filters: GetBulkChangeBillingRunsRequest) => void;
}

type FilterForm = Omit<GetBulkChangeBillingRunsRequest, 'recordNumber'> & {
  recordNumber: string;
};

const statusOptions: BulkChangeBillingRunStatus[] = [
  BulkChangeBillingRunStatus.Complete,
  BulkChangeBillingRunStatus.PartialFailure,
  BulkChangeBillingRunStatus.Failed,
  BulkChangeBillingRunStatus.Running,
  BulkChangeBillingRunStatus.NotStarted,
];

export const BulkChangeBillingRunsFilter: React.FC<Props> = ({
  filter,
  roomTypes,
  onFilterChange,
}) => {
  const form = useForm<FilterForm>({
    defaultValues: {
      status: undefined,
      corporateAccountId: undefined,
      roomTypeId: undefined,
      recordNumber: '',
    },
  });
  const { register, watch } = form;

  useEffect(() => {
    register({ name: 'status' });
    register({ name: 'corporateAccountId' });
    register({ name: 'roomTypeId' });
  }, [register]);

  const { status, corporateAccountId, roomTypeId, recordNumber } = watch();

  useDebouncedEffect(
    () => {
      onFilterChange({
        status: status || undefined,
        corporateAccountId: corporateAccountId || undefined,
        roomTypeId: roomTypeId || undefined,
        recordNumber: recordNumber ? parseInt(recordNumber, 10) : undefined,
      });
    },
    300,
    [status, corporateAccountId, roomTypeId, recordNumber]
  );

  return (
    <FilterSection>
      <FormContext {...form}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2}>
            <FormSelect name="status" label="Status" defaultValue={filter.status ?? ''} fullWidth>
              <MenuItem value="">All</MenuItem>
              {statusOptions.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormSelect
              name="roomTypeId"
              label="Room Type"
              defaultValue={filter.roomTypeId ?? ''}
              fullWidth
            >
              <MenuItem value="">All</MenuItem>
              {roomTypes.map(rt => (
                <MenuItem key={rt.id} value={rt.id}>
                  {rt.name}
                </MenuItem>
              ))}
            </FormSelect>
          </Grid>

          <Grid item xs={12} sm={3} md={2}>
            <FormTextField name="recordNumber" label="Record Number" type="number" fullWidth />
          </Grid>
        </Grid>
      </FormContext>
    </FilterSection>
  );
};
